import {
  Badge,
  Card,
  Center,
  Input,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import { ReactNode, cloneElement } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  LABELING_TYPES,
  LABELING_TYPES_CONFIG_MAP
} from '@/components/annotations/LabelingTypes/LabelingTypeConfig'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import { LabelingType } from '@/types/dataset'
import { MLModelType } from '@/types/model'

// Due to ML limitations, we temporarily disable the classification labeling type for primary (stage 1) models
const isPrimaryModelClassification = (
  labelingType: LabelingType,
  modelType: MLModelType
) => {
  return (
    labelingType === LabelingType.Classification &&
    modelType === MLModelType.Primary
  )
}

type LabelingTypeCardSelectProps = {
  modelType: MLModelType
  value?: LabelingType
  label?: ReactNode
  description?: ReactNode
  onChange: (labelingType: LabelingType) => void
}

export const LabelingTypeCardSelect = ({
  modelType,
  value,
  label,
  description,
  onChange
}: LabelingTypeCardSelectProps) => {
  return (
    <Input.Wrapper label={label} description={description}>
      <Paper bg="gray.1" p="xl" mt="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {LABELING_TYPES.map((labelingType) => {
            const { icon, name, description, hint } =
              LABELING_TYPES_CONFIG_MAP[labelingType]

            return (
              <Tooltip
                key={labelingType}
                disabled={
                  !isPrimaryModelClassification(labelingType, modelType)
                }
                label={
                  <FormattedMessage id="models.labelingType.stage2only.tooltip" />
                }
                w={320}
                multiline
              >
                <SelectableCard
                  height={240}
                  isSelected={labelingType === value}
                  disabled={isPrimaryModelClassification(
                    labelingType,
                    modelType
                  )}
                  onClick={() => onChange(labelingType)}
                >
                  <Card.Section pos="relative">
                    <Center h={70}>{cloneElement(icon, { size: 32 })}</Center>

                    {isPrimaryModelClassification(labelingType, modelType) && (
                      <Badge
                        pos="absolute"
                        top={8}
                        right={8}
                        size="xs"
                        color="brand-primary"
                      >
                        <FormattedMessage id="models.labelingType.stage2only.badge" />
                      </Badge>
                    )}
                  </Card.Section>

                  <Card.Section py="sm" inheritPadding>
                    <Stack gap="xs">
                      <Text size="sm" ta="center" fw="bold">
                        <FormattedMessage id={name} />
                      </Text>

                      <Text size="sm" ta="center">
                        <FormattedMessage id={description} />
                      </Text>

                      <Text size="xs" ta="center" c="dimmed">
                        <FormattedMessage id={hint} />
                      </Text>
                    </Stack>
                  </Card.Section>
                </SelectableCard>
              </Tooltip>
            )
          })}
        </SimpleGrid>
      </Paper>
    </Input.Wrapper>
  )
}
